<template>
  <div class="ivc-shell">
    <!-- Header -->
    <header class="ivc-header">
      <div class="ivc-header-left">
        <div class="ivc-icon-badge">
          <i :class="headerIcon"></i>
        </div>
        <div>
          <div class="ivc-title-row">
            <h2 class="ivc-title">{{ headerTitle }}</h2>
            <span class="ivc-chip" v-if="edit_data.serial_card">
              <span class="ivc-chip-dot"></span>{{ edit_data.serial_card }}
            </span>
          </div>
          <p class="ivc-subtitle">{{ headerSubtitle }}</p>
        </div>
      </div>
      <button type="button" class="ivc-close" aria-label="Close" :disabled="assigning" @click.prevent="$emit('cancel')">
        <i class="fa fa-times"></i>
      </button>
    </header>

    <!-- Scrollable body -->
    <div class="ivc-body">
      <section class="ivc-meta-card">
        <div class="ivc-meta-grid">
          <div class="ivc-meta-col">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.customer') }}</span>
            <span class="ivc-meta-value">{{ edit_data.customer_name || '-' }}</span>
          </div>
          <div class="ivc-meta-col ivc-meta-col-right">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.phoneNumber') }}</span>
            <span class="ivc-meta-value ivc-mono">{{ edit_data.contact_number || '-' }}</span>
          </div>

          <div class="ivc-meta-divider"></div>

          <div class="ivc-meta-col">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.serialCard') }}</span>
            <span class="ivc-meta-value-row">
              <span class="ivc-meta-value ivc-mono ivc-bold">{{ edit_data.serial_card }}</span>
              <span class="ivc-verified" v-if="edit_data.customer_id">
                <i class="fa fa-check"></i>{{ $t('customerMemberCard.verified') }}
              </span>
            </span>
          </div>
          <div class="ivc-meta-col ivc-meta-col-right">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.cardType') }}</span>
            <span class="ivc-meta-value">{{ edit_data.card_types || '-' }}</span>
          </div>

          <div class="ivc-meta-divider"></div>

          <template v-if="edit_data.promotion_type_id === 2">
            <div class="ivc-meta-col">
              <span class="ivc-meta-label">{{ $t('customerMemberCard.promotion') }}</span>
              <span class="ivc-meta-value">{{ edit_data.package_name || '-' }}</span>
            </div>
            <div class="ivc-meta-col ivc-meta-col-right">
              <span class="ivc-meta-label">{{ $t('customerMemberCard.maxTimeQuota') }}</span>
              <span class="ivc-meta-value ivc-mono ivc-bold">{{ edit_data.max_times }}</span>
            </div>
          </template>
          <div class="ivc-meta-col ivc-meta-col-span" v-else>
            <span class="ivc-meta-label">{{ $t('customerMemberCard.maxTimeQuota') }}</span>
            <span class="ivc-meta-value ivc-mono ivc-bold">{{ edit_data.max_times }}</span>
          </div>
        </div>
      </section>

      <!-- Step 1: link a customer (new or existing) -->
      <template v-if="step === 'form'">
        <div class="mc-mode-tabs">
          <button type="button" class="mc-mode-btn" :class="{ 'mc-mode-btn-active': customerMode === 'new' }"
            @click="setCustomerMode('new')">
            {{ $t('customerMemberCard.newCustomer') }}
          </button>
          <button type="button" class="mc-mode-btn" :class="{ 'mc-mode-btn-active': customerMode === 'old' }"
            @click="setCustomerMode('old')">
            {{ $t('customerMemberCard.existingCustomer') }}
          </button>
        </div>

        <!-- new customer -->
        <template v-if="customerMode === 'new'">
          <a-alert type="info" show-icon :message="$t('customerMemberCard.assignCustomerHint')" />

          <ts-loading-banner :loading="loading">
            <div class="row">
              <div class="col-md-12">
                <label class="ts-text-xs required">{{ $t('customerProfile.customerType') }}</label>
                <a-select v-model="model.cus_type_id" :options="listType" :dropdown-style="{ zIndex: 2200 }"
                  class="ts-w-full" :class="{ 'is-invalid': errors.has('cus_type_id') }"></a-select>
                <div class="validate ts-text-xs" v-if="errors.has('cus_type_id')">{{ errors.first('cus_type_id') }}
                </div>
              </div>
            </div>

            <div class="row ts-mt-2">
              <div class="col-md-12">
                <label class="ts-text-xs required">{{ $t('customerProfile.customerName') }}</label>
                <input v-model.trim="model.customer_name" type="text" class="form-control"
                  :class="{ 'is-invalid': errors.has('customer_name') }" />
                <div class="invalid-feedback ts-text-xs" v-if="errors.has('customer_name')">
                  {{ errors.first('customer_name') }}
                </div>
              </div>
            </div>

            <div class="row ts-mt-2">
              <div class="col-md-12">
                <a-radio-group v-model="model.gender" class="ts-p-3">
                  <a-radio value="Male">Male</a-radio>
                  <a-radio value="Female">Female</a-radio>
                  <a-radio value="Other">Other</a-radio>
                </a-radio-group>
                <div class="validate ts-text-xs" v-if="errors.has('gender')">{{ errors.first('gender') }}</div>
              </div>
            </div>

            <div class="row ts-mt-2">
              <div class="col-md-12">
                <label class="ts-text-xs required">{{ $t('customerProfile.phoneNumber') }}</label>
                <input v-model.trim="model.contact_number" type="text" class="form-control"
                  :class="{ 'is-invalid': errors.has('contact_number') }" />
                <div class="invalid-feedback ts-text-xs" v-if="errors.has('contact_number')">
                  {{ errors.first('contact_number') }}
                </div>
              </div>
            </div>

            <div class="row ts-mt-3">
              <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
                <ts-button :disabled="assigning" @click.prevent="$emit('cancel')">{{ $t('cancel') }}</ts-button>
                <ts-button color="primary" :waiting="assigning" @click.prevent="onSave">{{ $t('save') }}</ts-button>
              </div>
            </div>
          </ts-loading-banner>
        </template>

        <!-- existing customer -->
        <template v-else>
          <ts-loading-banner :loading="loading">
            <div class="row">
              <div class="col-md-12">
                <label class="ts-text-xs required">{{ $t('customerMemberCard.customer') }}</label>
                <a-select v-model="selectedCustomerId" :options="customerOptions" show-search allow-clear
                  option-filter-prop="children" :dropdown-style="{ zIndex: 2200 }" class="ts-w-full"
                  :class="{ 'is-invalid': errors.has('customer_id') }" :placeholder="$t('select')"></a-select>
                <div class="validate ts-text-xs" v-if="errors.has('customer_id')">{{ errors.first('customer_id') }}
                </div>
              </div>
            </div>

            <div class="row ts-mt-3">
              <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
                <ts-button :disabled="assigning" @click.prevent="$emit('cancel')">{{ $t('cancel') }}</ts-button>
                <ts-button color="primary" :waiting="assigning" @click.prevent="onAssignExisting">
                  {{ $t('customerMemberCard.assignCustomer') }}
                </ts-button>
              </div>
            </div>
          </ts-loading-banner>
        </template>
      </template>

      <!-- Step 2: confirm payment before the invoice is generated -->
      <template v-else>
        <div class="ivc-alert">
          <span class="ivc-alert-icon">!</span>
          <p>{{ $t('customerMemberCard.confirmInvoiceHint') }}</p>
        </div>

        <ts-loading-banner :loading="loading">
          <section class="ivc-breakdown">
            <div class="ivc-line">
              <span class="ivc-line-label">{{ $t('promotionPackage.quantity') }}</span>
              <span class="ivc-mono ivc-semibold">{{ preview.quantity }}</span>
            </div>
            <div class="ivc-line">
              <span class="ivc-line-label">{{ $t('promotionPackage.unitPrice') }}</span>
              <span class="ivc-mono ivc-semibold">{{ formatCurrency(preview.unit_price) }}</span>
            </div>
            <div class="ivc-line">
              <span class="ivc-line-label">{{ $t('promotionPackage.subTotal') }}</span>
              <span class="ivc-mono ivc-semibold">{{ formatCurrency(preview.sub_total) }}</span>
            </div>
            <div class="ivc-line">
              <span class="ivc-line-label">
                {{ $t('promotionPackage.discount') }}
                <span class="ivc-tag-applied" v-if="preview.discount_rate">{{ $t('customerMemberCard.applied') }}</span>
              </span>
              <span class="ivc-mono ivc-semibold ivc-text-emerald" v-if="preview.discount_rate">
                {{ preview.discount_rate }}{{ preview.discount_type === '$' ? '$' : '%' }}
                ({{ formatCurrency(preview.discount_amount) }})
              </span>
              <span class="ivc-mono ivc-semibold" v-else>-</span>
            </div>

            <div class="ivc-grand-total">
              <span class="ivc-grand-label">{{ $t('customerMemberCard.grandTotal') }}</span>
              <div class="ivc-grand-value">
                <span class="ivc-grand-currency">$</span>{{ formatNumber(preview.grand_total) }}
              </div>
            </div>
          </section>

          <!-- payment method -->
          <div class="ivc-tabs">
            <button type="button" class="ivc-tab" :class="{ 'ivc-tab-active': payment.payment_method_id === 1 }"
              @click="selectCash">
              <i class="fa fa-money-bill-wave"></i> {{ $t('restaurantSale.cashPayment') }}
            </button>
            <button type="button" class="ivc-tab" :class="{ 'ivc-tab-active': payment.payment_method_id === 3 }"
              @click="selectBank">
              <i class="fa fa-university"></i> {{ $t('martSale.bankTransfer') }}
            </button>
          </div>

          <!-- cash -->
          <template v-if="payment.payment_method_id === 1">
            <div class="ivc-form-grid">
              <div>
                <label class="ivc-label required">{{ $t('customerMemberCard.cashPaymentUsd') }}</label>
                <div class="ivc-input-wrap">
                  <span class="ivc-input-prefix">$</span>
                  <input v-model.number="payment.paid_in_usd" type="number" min="0"
                    class="ivc-input ivc-input-has-prefix" :class="{ 'is-invalid': errors.has('paid_in_usd') }" />
                </div>
                <div class="validate ts-text-xs" v-if="errors.has('paid_in_usd')">{{ errors.first('paid_in_usd') }}
                </div>
              </div>
              <div>
                <label class="ivc-label">{{ $t('martSale.couponInUsd') }}</label>
                <div class="ivc-input-wrap">
                  <i class="fa fa-ticket-alt ivc-input-prefix-icon"></i>
                  <input v-model.number="payment.paid_in_coupon" type="number" min="0"
                    class="ivc-input ivc-input-has-prefix" />
                </div>
              </div>
            </div>
          </template>

          <!-- bank transfer -->
          <template v-else>
            <div class="ivc-mt">
              <label class="ivc-label required">{{ $t('customerMemberCard.bankAccount') }}</label>
              <div class="ivc-bank-list" v-if="bankAccountOptions.length">
                <button type="button" class="ivc-bank-card" v-for="option in bankAccountOptions" :key="option.key"
                  :class="{ 'ivc-bank-card-active': isBankAccountSelected(option) }"
                  @click="selectBankAccount(option)">
                  <span class="ivc-bank-icon">
                    <img v-if="option.bank_logo && !brokenLogoKeys.includes(option.key)" :src="option.bank_logo"
                      :alt="option.bank_name" @error="onBankLogoError(option)" />
                    <i class="fa fa-university" v-else></i>
                  </span>
                  <span class="ivc-bank-info">
                    <span class="ivc-bank-name-row">
                      <span class="ivc-bank-name">{{ option.bank_name }}</span>
                      <span class="ivc-bank-sep">|</span>
                      <span class="ivc-bank-account-number ivc-mono">{{ option.account_number }}</span>
                    </span>
                    <span class="ivc-bank-account-name" v-if="option.account_name">{{ option.account_name }}</span>
                  </span>
                  <span class="ivc-bank-check" v-if="isBankAccountSelected(option)"><i class="fa fa-check"></i></span>
                </button>
              </div>
              <div class="ivc-hint" v-else>{{ $t('noData') }}</div>
              <div class="validate ts-text-xs" v-if="errors.has('bank_name') || errors.has('account_number')">
                {{ errors.first('bank_name') || errors.first('account_number') }}
              </div>
            </div>
            <div class="ivc-mt">
              <label class="ivc-label required">{{ $t('martSale.bankTransfer') }} USD</label>
              <div class="ivc-input-wrap">
                <span class="ivc-input-prefix">$</span>
                <input v-model.number="payment.bank_transfer" type="number" min="0"
                  class="ivc-input ivc-input-has-prefix" :class="{ 'is-invalid': errors.has('bank_transfer') }" />
              </div>
              <div class="validate ts-text-xs" v-if="errors.has('bank_transfer')">
                {{ errors.first('bank_transfer') }}
              </div>
            </div>
          </template>

          <div class="ivc-change-row" v-if="payment.payment_method_id === 1"
            :class="{ 'ivc-change-negative': changeAmount < 0 }">
            <div class="ivc-change-left">
              <span class="ivc-change-dot"></span>
              <span>{{ $t('customerMemberCard.cashBackChange') }}</span>
            </div>
            <span class="ivc-mono ivc-change-value">{{ formatCurrency(changeAmount) }}</span>
          </div>
        </ts-loading-banner>
      </template>
    </div>

    <!-- Sticky footer for the payment confirmation step -->
    <footer class="ivc-footer" v-if="step === 'confirm'">
      <div class="ivc-footer-status" :class="{ 'ivc-footer-status-bad': changeAmount < 0 }">
        <span class="ivc-status-dot"></span>
        <span>{{ footerStatusText }}</span>
      </div>
      <div class="ivc-footer-actions">
        <button type="button" class="ivc-btn ivc-btn-secondary" :disabled="assigning" @click.prevent="$emit('cancel')">
          {{ $t('cancel') }}
        </button>
        <button type="button" class="ivc-btn ivc-btn-primary" :disabled="changeAmount < 0 || assigning"
          @click.prevent="onConfirm">
          {{ $t('confirm') }}
          <i class="fa fa-check"></i>
        </button>
      </div>
    </footer>

    <Spin size="large" fix v-if="assigning"></Spin>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'assignCustomerToMemberCard',
  data() {
    return {
      step: 'form',
      customerMode: 'new',
      selectedCustomerId: null,
      loading: false,
      assigning: false,
      errors: new Errors(),
      preview: {
        quantity: 0,
        unit_price: 0,
        sub_total: 0,
        discount_type: '%',
        discount_rate: 0,
        discount_amount: 0,
        grand_total: 0
      },
      bankProfiles: [],
      brokenLogoKeys: [],
      payment: {
        payment_method_id: 1,
        paid_in_usd: 0,
        paid_in_coupon: 0,
        bank_transfer: 0,
        bank_name: null,
        account_number: null,
        account_name: null
      },
      model: {
        cus_type_id: 2,
        customer_name: null,
        gender: null,
        contact_number: null,
        // Non-nullable on the backend DTO — must be sent even though this
        // trimmed form doesn't surface them as inputs.
        is_general_customer: false,
        is_membership: false,
        is_allow_update_gps_location: false,
        membership_discount_rate: 0,
        // approve_status is NOT NULL on setup_customer_profiles; 2 = Approved
        // (no pending approval workflow) for a quick-created customer.
        approve_status: 2
      }
    }
  },
  created() {
    if (this.edit_data.customer_id && !this.edit_data.ref_stock_out_id) {
      this.step = 'confirm'
      this.loadPreview()
    } else {
      this.fetchResources()
    }
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['edit_data', 'formViewData']),
    headerTitle() {
      return this.step === 'confirm'
        ? this.$t('customerMemberCard.generateInvoice')
        : this.$t('customerMemberCard.assignCustomer')
    },
    headerSubtitle() {
      if (this.step === 'confirm') {
        return this.$t('customerMemberCard.confirmInvoiceSubtitle')
      }
      return this.customerMode === 'new'
        ? this.$t('customerMemberCard.assignCustomerHint')
        : this.$t('customerMemberCard.selectCustomerHint')
    },
    headerIcon() {
      return this.step === 'confirm' ? 'fas fa-file-invoice-dollar' : 'fa fa-user-plus'
    },
    footerStatusText() {
      return this.changeAmount < 0
        ? this.$t('customerMemberCard.insufficientAmount')
        : this.$t('customerMemberCard.readyToConfirm')
    },
    listType() {
      return this.$store.state.customer.profile.customerType.map(el => ({
        value: el.cus_type_id,
        label: el.customer_type
      }))
    },
    customerOptions() {
      if (this.formViewData.customer && this.formViewData.customer.length) {
        return this.formViewData.customer.map(el => ({
          label:
            'Code: ' +
            el.customer_code +
            ' | Customer: ' +
            el.customer_name +
            ' | Contact: ' +
            el.contact_number +
            ' | Gender: ' +
            el.gender,
          value: el.customer_id
        }))
      }
      return []
    },
    bankAccountOptions() {
      const list = []
      this.bankProfiles.forEach(bank => {
        (bank.accounts || []).forEach(account => {
          list.push({
            key: bank.bank_name + '-' + account.account_number,
            bank_name: bank.bank_name,
            bank_logo: bank.bank_logo,
            account_number: account.account_number,
            account_name: account.account_name
          })
        })
      })
      return list
    },
    changeAmount() {
      const paid =
        parseFloat(this.payment.paid_in_usd || 0) +
        parseFloat(this.payment.bank_transfer || 0) +
        parseFloat(this.payment.paid_in_coupon || 0)
      return Math.round((paid - (this.preview.grand_total || 0)) * 100) / 100
    }
  },
  methods: {
    ...mapActions('customer/customerMemberCard', ['getFormViewData']),
    formatCurrency(value) {
      return '$ ' + this.formatNumber(value)
    },
    formatNumber(value) {
      return parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fetchResources() {
      this.loading = true
      this.$store.dispatch('customer/profile/getCustomerType').finally(() => {
        this.loading = false
      })
    },
    setCustomerMode(mode) {
      this.customerMode = mode
      this.errors = new Errors()
      if (mode === 'old' && !this.customerOptions.length) {
        this.fetchExistingCustomers()
      }
    },
    fetchExistingCustomers() {
      this.loading = true
      this.getFormViewData({ params: undefined, index: undefined }).finally(() => {
        this.loading = false
      })
    },
    onAssignExisting() {
      if (!this.selectedCustomerId) {
        this.errors = new Errors({ customer_id: ['Please select a customer.'] })
        return
      }
      this.errors = new Errors()
      this.assigning = true
      this.assignToCard({ customer_id: this.selectedCustomerId })
    },
    loadPreview() {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/previewInvoice', this.edit_data.customer_member_card_id)
        .then(response => {
          this.preview = response.data
          this.payment.paid_in_usd = this.preview.grand_total
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectCash() {
      this.payment.payment_method_id = 1
      this.payment.bank_transfer = 0
      this.payment.bank_name = null
      this.payment.account_number = null
      this.payment.account_name = null
      this.payment.paid_in_usd = this.preview.grand_total
    },
    selectBank() {
      this.payment.payment_method_id = 3
      this.payment.paid_in_usd = 0
      this.payment.paid_in_coupon = 0
      this.payment.bank_transfer = this.preview.grand_total
      this.fetchBankProfiles()
    },
    fetchBankProfiles() {
      if (this.bankProfiles.length) {
        this.autoSelectBankAccount()
        return
      }
      this.$store
        .dispatch('restaurantSale/counterCheckout/bankProfile')
        .then(response => {
          this.bankProfiles = response.data
          this.autoSelectBankAccount()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    autoSelectBankAccount() {
      if (this.bankAccountOptions.length === 1) {
        this.selectBankAccount(this.bankAccountOptions[0])
      }
    },
    selectBankAccount(option) {
      this.payment.bank_name = option.bank_name
      this.payment.account_number = option.account_number
      this.payment.account_name = option.account_name
    },
    isBankAccountSelected(option) {
      return this.payment.bank_name === option.bank_name && this.payment.account_number === option.account_number
    },
    onBankLogoError(option) {
      if (!this.brokenLogoKeys.includes(option.key)) {
        this.brokenLogoKeys.push(option.key)
      }
    },
    onSave() {
      this.assigning = true
      this.errors = new Errors()
      this.$store
        .dispatch('customer/profile/store', this.model)
        .then(response => this.assignToCard(response.data))
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
          this.assigning = false
        })
    },
    assignToCard(customer) {
      this.$store
        .dispatch('customer/customerMemberCard/assignCustomer', {
          id: this.edit_data.customer_member_card_id,
          data: { customer_id: customer.customer_id }
        })
        .then(response => {
          this.preview = response.data.preview
          this.payment.paid_in_usd = this.preview.grand_total
          this.step = 'confirm'
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.assigning = false
        })
    },
    onConfirm() {
      this.assigning = true
      this.errors = new Errors()
      this.$store
        .dispatch('customer/customerMemberCard/generateInvoice', {
          id: this.edit_data.customer_member_card_id,
          data: this.payment
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('assigned', response.data)
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.assigning = false
        })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap');

.ivc-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 96vh;
  border-radius: 14px;
  overflow: hidden;
  font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header */
.ivc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}

.ivc-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ivc-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  color: #3182ce;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.ivc-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ivc-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.01em;
}

.ivc-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.ivc-chip-dot {
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 9999px;
  background: #059669;
}

.ivc-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0;
}

.ivc-close {
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  outline: none;
}

.ivc-close:hover {
  color: #334155;
  background: #f1f5f9;
}

.ivc-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Body */
.ivc-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
}

.ivc-body>*+* {
  margin-top: 14px;
}

/* Metadata card */
.ivc-meta-card {
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
}

.ivc-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 10px;
}

.ivc-meta-col {
  display: flex;
  flex-direction: column;
}

.ivc-meta-col-right {
  align-items: flex-end;
  text-align: right;
}

.ivc-meta-col-span {
  grid-column: 1 / -1;
}

.ivc-meta-label {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.ivc-meta-value {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 2px;
}

.ivc-meta-value-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.ivc-meta-divider {
  grid-column: 1 / -1;
  border-top: 1px solid rgba(226, 232, 240, 0.7);
  margin: 1px 0;
}

.ivc-verified {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  background: #dbeafe;
  color: #2b6cb0;
}

.ivc-mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.ivc-bold {
  font-weight: 700;
}

.ivc-semibold {
  font-weight: 600;
}

/* Alert banner */
.ivc-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 251, 235, 0.9);
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 12px;
  color: #92400e;
}

.ivc-alert p {
  margin: 0;
  line-height: 1.35;
}

.ivc-alert-icon {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  background: #fde68a;
  color: #78350f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

/* Financial breakdown */
.ivc-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ivc-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #475569;
  padding: 0 2px;
}

.ivc-line-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
}

.ivc-tag-applied {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  background: #d1fae5;
  color: #047857;
}

.ivc-text-emerald {
  color: #059669 !important;
}

.ivc-grand-total {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(239, 246, 255, 0.8);
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 10px 14px;
}

.ivc-grand-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #1e3a8a;
}

.ivc-grand-value {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 18px;
  font-weight: 700;
  color: #3182ce;
  letter-spacing: -0.02em;
}

.ivc-grand-currency {
  font-size: 13px;
  margin-right: 2px;
}

/* Payment method tabs */
.ivc-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  margin-top: 14px;
  background: #f1f5f9;
  border-radius: 12px;
}

.ivc-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #475569;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ivc-tab:hover {
  background: rgba(226, 232, 240, 0.7);
}

.ivc-tab:focus-visible {
  box-shadow: 0 0 0 2px rgba(49, 130, 206, 0.35);
}

.ivc-tab-active,
.ivc-tab-active:hover {
  background: #3182ce;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

/* Cash / coupon inputs */
.ivc-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 14px;
}

.ivc-mt {
  margin-top: 14px;
}

.ivc-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.ivc-hint {
  display: block;
  font-size: 12px;
  color: #94a3b8;
}

/* Combined bank + account selector */
.ivc-bank-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}

.ivc-bank-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ivc-bank-card:hover {
  border-color: #bfdbfe;
}

.ivc-bank-card-active {
  border-color: #3182ce;
  background: rgba(239, 246, 255, 0.6);
  box-shadow: 0 2px 8px rgba(49, 130, 206, 0.18);
}

.ivc-bank-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #3182ce;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  overflow: hidden;
}

.ivc-bank-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ivc-bank-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.ivc-bank-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ivc-bank-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.ivc-bank-sep {
  color: #cbd5e1;
}

.ivc-bank-account-number {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.ivc-bank-account-name {
  font-size: 12px;
  color: #64748b;
}

.ivc-bank-check {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: #3182ce;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.ivc-input-wrap {
  position: relative;
}

.ivc-input-prefix {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 12px;
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 13px;
  font-weight: 600;
  pointer-events: none;
}

.ivc-input-prefix-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 12px;
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}

.ivc-input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-weight: 600;
  color: #0f172a;
}

.ivc-input-has-prefix {
  padding-left: 28px;
}

.ivc-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.15);
}

.ivc-input.is-invalid {
  border-color: #e53e3e;
}

/* Cash back / change */
.ivc-change-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
}

.ivc-change-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.ivc-change-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #3182ce;
}

.ivc-change-value {
  font-size: 16px;
  font-weight: 700;
  color: #3182ce;
}

.ivc-change-negative .ivc-change-value {
  color: #e53e3e;
}

.ivc-change-negative .ivc-change-dot {
  background: #e53e3e;
}

/* Footer */
.ivc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.ivc-footer-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.ivc-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #38a169;
}

.ivc-footer-status-bad {
  color: #e53e3e;
}

.ivc-footer-status-bad .ivc-status-dot {
  background: #e53e3e;
}

.ivc-footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ivc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ivc-btn:focus-visible {
  box-shadow: 0 0 0 2px rgba(49, 130, 206, 0.35);
}

.ivc-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ivc-btn-secondary {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.ivc-btn-secondary:hover:not(:disabled) {
  background: #f1f5f9;
}

.ivc-btn-primary {
  background: #3182ce;
  border: 1px solid #3182ce;
  color: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.1);
}

.ivc-btn-primary:hover:not(:disabled) {
  background: #2b6cb0;
}

/* Step 1 mode tabs (assign customer) — unchanged from the previous design */
.mc-mode-tabs {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.mc-mode-btn {
  border: none;
  background: #fff;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
}

.mc-mode-btn+.mc-mode-btn {
  border-left: 1px solid #d1d5db;
}

.mc-mode-btn-active {
  background: #2563eb;
  color: #fff;
}
</style>
